import React, { useState } from 'react';
import { useNovelUpdate } from '../hooks/useNovelUpdate';
import './CharactersCategory.css';

const CharactersCategory = ({ novelId, novel, setNovel, refreshNovel, isProduction = false }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use data from novel prop instead of local state
  const characters = novel?.characters || [];
  const flags = novel?.states?.flags || [];
  const scales = novel?.states?.scales || [];
  const spriteAssets = novel?.assets?.sprites || [];

  const { updateNovel } = useNovelUpdate(novelId, isProduction, refreshNovel);

  const handleCreateCharacter = async (characterData) => {
    setError(null);
    setLoading(true);

    if (!characterData.prompt_name) {
      setError('Имя персонажа обязательно');
      setLoading(false);
      return;
    }

    try {
      const newCharacter = {
        prompt_name: characterData.prompt_name,
        sprites: characterData.sprites,
        biographies: characterData.biographies,
        name_translations: []
      };

      const updatedCharacters = [...characters, newCharacter];

      const updatedNovel = {
        ...novel,
        characters: updatedCharacters
      };

      await updateNovel(updatedNovel);
      setShowCreateForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCharacter = async (characterData) => {
    setError(null);
    setLoading(true);

    if (!characterData.prompt_name) {
      setError('Имя персонажа обязательно');
      setLoading(false);
      return;
    }

    try {
      const updatedCharacter = {
        ...editingCharacter,
        prompt_name: characterData.prompt_name,
        sprites: characterData.sprites,
        biographies: characterData.biographies
      };

      const updatedCharacters = characters.map(character =>
        character.id === editingCharacter.id ? updatedCharacter : character
      );

      const updatedNovel = {
        ...novel,
        characters: updatedCharacters
      };

      await updateNovel(updatedNovel);
      setEditingCharacter(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCharacter = async (characterId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого персонажа?')) {
      return;
    }

    try {
      setLoading(true);
      const updatedCharacters = characters.filter(character => character.id !== characterId);

      const updatedNovel = {
        ...novel,
        characters: updatedCharacters
      };

      await updateNovel(updatedNovel);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ch-wrap">
      {/* Description */}
      <div className="ch-desc">
        <p>{'Персонажи - это личности, которые взаимодействуют с игроками. Каждый персонаж может иметь несколько наборов спрайтов и условные биографии.'}</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="ch-error">
          {error}
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <CharacterForm
          isEdit={false}
          spriteAssets={novel?.assets?.sprites || []}
          flags={novel?.states?.flags || []}
          scales={novel?.states?.scales || []}
          novel={novel}
          onSubmit={handleCreateCharacter}
          onCancel={() => setShowCreateForm(false)}
          loading={loading}
          novelId={novelId}
          isProduction={isProduction}
        />
      )}

      {/* Edit Form */}
      {editingCharacter && (
        <CharacterForm
          isEdit={true}
          character={editingCharacter}
          spriteAssets={novel?.assets?.sprites || []}
          flags={novel?.states?.flags || []}
          scales={novel?.states?.scales || []}
          novel={novel}
          onSubmit={handleEditCharacter}
          onCancel={() => setEditingCharacter(null)}
          loading={loading}
          novelId={novelId}
          isProduction={isProduction}
        />
      )}

      {/* Controls */}
      <button
        className="ch-add-btn"
        onClick={() => setShowCreateForm(true)}
      >
        <span>➕</span>
        {'Добавить персонажа'}
      </button>

      {/* Characters List */}
      <div className="ch-list">
        {characters.length > 0 ? (
          characters.map((character) => (
            <div key={character.id} className="ch-card">
              <div className="ch-card-head">
                <h4 className="ch-card-name">{character.prompt_name}</h4>
                <div className="ch-card-actions">
                  <button
                    className="ch-btn-edit"
                    onClick={() => setEditingCharacter(character)}
                    disabled={loading}
                  >
                    {'Редактировать'}
                  </button>
                  <button
                    className="ch-btn-delete"
                    onClick={() => handleDeleteCharacter(character.id)}
                    disabled={loading}
                  >
                    {'Удалить'}
                  </button>
                </div>
              </div>
              <div className="ch-card-meta">
                <div className="character-sprite-mixes">
                  <strong>{'Наборы спрайтов'}:</strong>
                  <span className="mixes-count">{character.sprites?.length || 0} наборов</span>
                </div>
                <div className="character-biography">
                  <strong>{'Биография'}:</strong>
                  <span className="biography-status">{character.biographies?.length > 0 ? '✅' : '❌'}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="ch-empty">
            <p>{'Персонажей пока нет. Создайте своего первого персонажа!'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const CharacterForm = ({ isEdit, character, spriteAssets, flags, scales, novel, onSubmit, onCancel, loading, novelId, isProduction = false }) => {
  const [formData, setFormData] = useState({
    prompt_name: character?.prompt_name || '',
    sprites: character?.sprites || [],
    biographies: character?.biographies || []
  });


  const addSpriteMix = () => {
    const newSpriteMix = {
      name: '',
      sprite_ids: []
    };
    setFormData({
      ...formData,
      sprites: [...formData.sprites, newSpriteMix]
    });
  };

  const updateSpriteMix = (index, field, value) => {
    const updatedSprites = [...formData.sprites];
    updatedSprites[index] = {
      ...updatedSprites[index],
      [field]: value
    };
    setFormData({
      ...formData,
      sprites: updatedSprites
    });
  };

  const removeSpriteMix = (index) => {
    const updatedSprites = formData.sprites.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      sprites: updatedSprites
    });
  };

  const addBiography = () => {
    const newBiography = {
      prompt_description: '',
      conditionals: []
    };
    setFormData({
      ...formData,
      biographies: [newBiography]
    });
  };

  const updateBiography = (field, value) => {
    const updatedBiography = {
      ...formData.biographies[0],
      [field]: value
    };
    setFormData({
      ...formData,
      biographies: [updatedBiography]
    });
  };

  const removeBiography = () => {
    setFormData({
      ...formData,
      biographies: []
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="ch-overlay">
      <div className="ch-modal">
        <h3 className="ch-modal-title">{isEdit ? 'Редактировать персонажа' : 'Добавить персонажа'}</h3>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={formData.prompt_name}
            onChange={(e) => setFormData({ ...formData, prompt_name: e.target.value })}
            placeholder="Имя персонажа"
            required
            disabled={loading}
            className="ch-input"
          />

          {/* Sprite Mixes Section */}
          <div className="ch-section">
            <div className="ch-section-head">
              <h4 className="ch-section-title">Наборы спрайтов</h4>
              <button
                type="button"
                onClick={addSpriteMix}
                disabled={loading || spriteAssets.length === 0}
                className="ch-add-section-btn"
              >
                ➕ Добавить набор спрайтов
              </button>
            </div>

            {formData.sprites.map((spriteMix, index) => (
              <div key={index} className="ch-sprite-mix">
                <div className="ch-sprite-mix-head">
                  <input
                    type="text"
                    value={spriteMix.name}
                    onChange={(e) => updateSpriteMix(index, 'name', e.target.value)}
                    placeholder="Название набора спрайтов"
                    disabled={loading}
                    className="ch-sprite-mix-input"
                  />
                  <div className="sprite-mix-buttons">
                    <button
                      type="button"
                      onClick={() => removeSpriteMix(index)}
                      disabled={loading}
                      className="ch-remove-mix-btn"
                    >
                      Удалить
                    </button>
                  </div>
                </div>

                <div className="sprite-selection">
                  <label className="ch-sprite-label">Выберите спрайты:</label>
                  <div className="ch-checkboxes">
                    {spriteAssets.map((sprite) => (
                      <div key={sprite.id} className="ch-checkbox-item">
                        <input
                          type="checkbox"
                          checked={spriteMix.sprite_ids.includes(sprite.id)}
                          onChange={(e) => {
                            let newSpriteIds = [...spriteMix.sprite_ids];
                            if (e.target.checked) {
                              newSpriteIds.push(sprite.id);
                            } else {
                              newSpriteIds = newSpriteIds.filter(id => id !== sprite.id);
                            }
                            updateSpriteMix(index, 'sprite_ids', newSpriteIds);
                          }}
                          disabled={loading}
                        />
                        <span className="checkbox-text">{sprite.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Biography Section */}
          <div className="ch-section">
            <div className="ch-section-head">
              <h4 className="ch-section-title">Биография</h4>
              {formData.biographies.length === 0 ? (
                <button
                  type="button"
                  onClick={addBiography}
                  disabled={loading}
                  className="ch-add-section-btn"
                >
                  ➕ Добавить биографию
                </button>
              ) : (
                <button
                  type="button"
                  onClick={removeBiography}
                  disabled={loading}
                  className="ch-remove-section-btn"
                >
                  Удалить биографию
                </button>
              )}
            </div>

            {formData.biographies.length > 0 && (
              <div className="biography-item">
                <div className="ch-bio-textarea-wrap">
                  <textarea
                    value={formData.biographies[0].prompt_description}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 2500) {
                        updateBiography('prompt_description', value);
                      }
                    }}
                    placeholder="Описание биографии"
                    disabled={loading}
                    className="ch-textarea"
                    rows={3}
                    maxLength={2500}
                  />
                  <div className="ch-char-count">
                    {formData.biographies[0].prompt_description.length}/2500
                  </div>
                </div>

              </div>
            )}
          </div>

          <div className="ch-modal-actions">
            <button
              type="submit"
              disabled={loading}
              className="ch-btn-submit"
            >
              {loading ? 'Загрузка...' : (isEdit ? 'Сохранить' : 'Создать')}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="ch-btn-cancel"
            >
              Отменить
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CharactersCategory;
